import { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { useAuth } from '../../../../utils/Auth';
import { useTypeHeaderIcon } from '../../../../utils/hooks/useTypeHeaderIcon';
import Tags from './Tags';
import Categories from './Categories';
import styles from './Details.module.css';

export default function Details() {

    const navigate = useNavigate();
    const { qr_data, orgObj } = useAuth();
    const [show, setShow] = useState('');
    const containerRef = useRef();

    const qr_type = useMemo(() => qr_data?.data?.qr_type || '', [qr_data?.data?.qr_type]);
    const category = useMemo(() => orgObj?.categories?.find(c => c.id === qr_data?.data?.category)?.name || '', [qr_data?.data?.category, orgObj?.categories]);
    const tags = useMemo(() => qr_data?.data?.tags || [], [qr_data?.data?.tags]);
    const total = useMemo(() => {
        const scanCount = qr_data?.data?.scanCount_cache || 0;
        return scanCount;
    },[qr_data?.data?.scanCount_cache]);

    const { header, icon } = useTypeHeaderIcon(qr_type);

    const handleNavigateLinked = () => navigate(`/collection/${qr_data?.id}/linked`);

    useEffect(() => {
        let mounted = true;
        const handleClickOutside = (e) => {
            if (show && containerRef.current && !containerRef.current.contains(e.target)) {
                mounted && setShow('');
            }
        };

        if (show) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            mounted = false;
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [show]);

    return (
        <div className={`${styles.container} ${show ? styles.expanded : ''}`}>
            <AnimatePresence initial={false} mode="wait">
                {show === 'categories' ? (
                    <motion.div
                        key="details-categories"
                        className={styles.overlay}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.25, ease: 'easeOut' }}
                        ref={containerRef}
                    >
                        <Categories />
                    </motion.div>
                ) : show === 'tags' ? (
                    <motion.div
                        key="details-tags"
                        className={styles.overlay}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.25, ease: 'easeOut' }}
                        ref={containerRef}
                    >
                        <Tags />
                    </motion.div>
                ) : (
                    <motion.div
                        key="details-default"
                        className={styles.grid}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                    >
                        <div className={styles.info}>
                            <p className={styles.infoTitle}>QR Type</p>
                            <span title={header} className={styles.infoType}>
                                <i className={icon} />
                                {header}
                            </span>
                        </div>
                        <div className={styles.info}>
                            <p className={styles.infoTitle}>Scan Count</p>
                            <span className={styles.infoTotal}>{total}</span>
                        </div>
                        <div className={`${styles.info} ${styles.clickable}`} onClick={() => setShow('categories')}>
                            <p className={styles.infoTitle}>Category</p>
                            <span className={styles.infoCat}>{category || <span>No Category</span>}</span>
                        </div>
                        <div className={`${styles.info} ${styles.clickable}`} onClick={() => setShow('tags')}>
                            <p className={styles.infoTitle}>Tag(s)</p>
                            <span className={styles.infoTags}>{tags?.join(', ') || <span>No Tags</span>}</span>
                        </div>
                        {qr_data?.data?.children?.length > 0 && (
                            <div className={`${styles.info} ${styles.clickable}`} onClick={handleNavigateLinked}>
                                <p className={styles.infoTitle}>Linked QR(s)</p>
                                <span className={styles.infoTags}>{`${qr_data?.data?.children?.length}` || <span>No LinkedQRs</span>}</span>
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
